import { useEffect, useRef } from "react"
import { usePage } from "@inertiajs/react"
import { toast } from "sonner"
import type { SharedProps } from "@/types"

/**
 * Hook that displays toast notifications for flash messages from Rails.
 * Uses Sonner for toast display with deduplication via toast IDs.
 */
export function useFlashToast() {
  const { flash } = usePage<SharedProps>().props
  const processedRef = useRef<string | null>(null)

  useEffect(() => {
    // Generate a unique key for this flash combination
    const flashKey = JSON.stringify(flash)

    // Skip if empty or already processed this exact flash
    const hasContent = flash.success || flash.error || flash.warning || flash.notice || flash.alert
    if (!hasContent || processedRef.current === flashKey) {
      return
    }

    // Mark as processed
    processedRef.current = flashKey

    // Use toast IDs to prevent duplicates (Sonner will dedupe same IDs)
    if (flash.success) {
      toast.success(flash.success, { id: `flash-success-${flash.success}` })
    }

    if (flash.error) {
      toast.error(flash.error, { id: `flash-error-${flash.error}` })
    }

    if (flash.warning) {
      toast.warning(flash.warning, { id: `flash-warning-${flash.warning}` })
    }

    if (flash.notice) {
      toast.info(flash.notice, { id: `flash-notice-${flash.notice}` })
    }

    if (flash.alert) {
      toast.error(flash.alert, { id: `flash-alert-${flash.alert}` })
    }
  }, [flash])
}
